import {
    Input,
    Button,
    Typography,
} from "@material-tailwind/react";
import Toaster from "../../components/Toaster";
import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeftIcon, DocumentPlusIcon, XCircleIcon } from "@heroicons/react/24/solid";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from '@hookform/resolvers/yup';
import Select from 'react-select';
import ShowLoader from "@/components/Loader";
import { fetchCountry, fetchCity, fetchOffice, addMember, resetStatus } from "@/store/slices/memberSlice";
import { getManager, fetchRole, fetchRolePermission } from "@/store/slices/internalUsersSlice";
import { fetchCountryMobileCode } from "../../store/slices/internalUsersSlice";
import FilePreview from "@/components/FilePreview";
import ScrollToTop from "@/components/ScrollToTop";
import {
    CountryOption,
    CountrySingleValue
} from "@/components/CountrySelectComponents";
import { useTranslation } from "react-i18next";

const fileValidation = (field, isEdit, isImageRemoved, t) =>
    yup
        .mixed()
        .test("fileType", t('fileValidation.imageOnly'), (value) => {
            if (!value || !(value instanceof File)) return true;
            return value.type.startsWith("image/");
        })
        .nullable()
        .test(
            "required-check",
            field === "id_proof"
                ? t('fileValidation.residenceIdRequired')
                : field === "profile_image"
                    ? t('fileValidation.profileImageRequired')
                    : t('fileValidation.fieldRequired', { fieldName: field.replaceAll("_", " ").replace(/^./, (str) => str.toUpperCase()) }),
            (value) => {
                if (!isEdit) return value instanceof File;
                if (isImageRemoved[field]) return value instanceof File;
                return true;
            }
        );

const noHTMLRegex = /^(?!.*<[^>]*>).*$/;
const noCodeRegex = /^(?!.*(javascript:|<script|<\/script>|<style|<\/style>|<iframe|<\/iframe>)).*$/i;
const notBlankRegex = /\S/;
const noConsecutiveSpecialChars = /^(?!.*([!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?])\1).*$/;
const noLeadingTrailingPunct = /^(?!^[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?])(?<![!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]$).*$/;

const schema = (isEdit = false, isImageRemoved = false, t) =>
    yup.object({
        first_name: yup
            .string()
            .trim()
            .required(t('validation.firstName.required'))
            .matches(notBlankRegex, t('validation.firstName.noEmpty'))
            .matches(noHTMLRegex, t('validation.firstName.noHtml'))
            .matches(noCodeRegex, t('validation.firstName.noCode'))
            .min(2, t('validation.firstName.min'))
            .max(30, t('validation.firstName.max')),

        email: yup
            .string()
            .trim()
            .required(t('validation.email.required'))
            .email(t('validation.email.invalid'))
            .matches(notBlankRegex, t('validation.email.noEmpty'))
            .matches(noHTMLRegex, t('validation.email.noHtml'))
            .matches(noCodeRegex, t('validation.email.noCode'))
            .max(50, t('validation.email.max')),

        mobile_number: yup
            .string()
            .trim()
            .required(t('validation.mobile.required'))
            .matches(notBlankRegex, t('validation.mobile.noEmpty'))
            .matches(/^\d+$/, t('validation.mobile.onlyDigits'))
            .min(8, t('validation.mobile.min'))
            .max(15, t('validation.mobile.max'))
            .test(
                "not-all-zeros",
                t('validation.mobile.noAllZeros'),
                (value) => {
                    if (!value) return false;
                    return !/^0+$/.test(value);
                }
            ),

        country_code: yup
            .string()
            .trim()
            .required(t('validation.countryCode.required'))
            .max(5, t('validation.countryCode.max')),

        alternate_number: yup
            .string()
            .nullable()
            .transform((value, originalValue) => (originalValue === "" ? null : value))
            .trim()
            .test("not-blank", t('validation.alternateMobile.noEmpty'), (value) => !value || notBlankRegex.test(value))
            .test("is-valid", t('validation.alternateMobile.invalid'), (value) => !value || /^[0-9]{8,15}$/.test(value))
            .test("not-all-zeros", t('validation.alternateMobile.noAllZeros'), (value) => !value || !/^0+$/.test(value)),

        alternate_country_code: yup
            .string()
            .nullable()
            .trim()
            .max(5, t('validation.alternateCountryCode.max'))
            .transform((value, originalValue) => (originalValue === "" ? null : value))
            .when("alternate_number", {
                is: (val) => val && val.trim() !== "",
                then: (schema) => schema.required(t('validation.alternateCountryCode.required')),
                otherwise: (schema) => schema.nullable(),
            }),

        profile_image: fileValidation("profile_image", isEdit, isImageRemoved, t),
        id_proof: fileValidation("id_proof", isEdit, isImageRemoved, t),

        residence_id: yup
            .string()
            .trim()
            .required(t('validation.residenceId.required'))
            .matches(notBlankRegex, t('validation.residenceId.noEmpty'))
            .matches(noHTMLRegex, t('validation.firstName.noHtml'))
            .matches(noCodeRegex, t('validation.firstName.noCode'))
            .matches(noConsecutiveSpecialChars, t('validation.residenceId.noConsecutiveSpecialChars'))
            .matches(noLeadingTrailingPunct, t('validation.residenceId.noLeadingTrailingPunct'))
            .min(2, t('validation.residenceId.minMax'))
            .max(30, t('validation.residenceId.minMax')),

        role_id: yup.string().trim().required(t('validation.role.required')),
        country_id: yup.string().trim().required(t('validation.country.required')),
        city_id: yup.string().trim().required(t('validation.city.required')),
        office_id: yup.string().trim().required(t('validation.office.required')),

    });


const isImageRemoved = {
    id_proof: false,
    profile_image: false,
};

export function AddMember({ isEdit = false, isView = false }) {
    const { t } = useTranslation("member");
    const { t: ct } = useTranslation("common");
    const navigate = useNavigate();
    const { id } = useParams();
    const dispatch = useDispatch();
    const [isShowLoader, setIsShowLoader] = useState(true);

    const { user } = useSelector((state) => state.user);
    const type = user?.roles ? user?.roles[0] : '';


    const initialForm = {
        id: null,
        type: type,
        status: 'active',
        first_name: '',
        email: '',
        mobile_number: null,
        country_code: null,
        alternate_number: null,
        alternate_country_code: null,
        designation: 'member',
        profile_image: '',
        residence_id: null,
        id_proof: '',
        role_id: null,
        country_id: null,
        city_id: null,
        office_id: null,
        permissions: []
    };

    const { register, handleSubmit, control, setValue, watch, formState: { errors }, reset, setError } = useForm({
        resolver: yupResolver(schema(isEdit, isImageRemoved, t)),
        defaultValues: initialForm
    });

    const selectedMember = useSelector((s) => s.member.selected);

    useEffect(() => {
        dispatch(fetchRole(type));
        dispatch(fetchCountry());
        dispatch(getManager(type));
        dispatch(fetchCountryMobileCode());
        if (!isEdit) {
            setIsShowLoader(false);
        }
    }, [dispatch]);

    const { cities, countries, offices, loading } = useSelector((state) => state.member);
    const { mobile_country_code } = useSelector((state) => state.internalUser);

    const { managers, role_permissions, roles } = useSelector((state) => state.internalUser);


    useEffect(() => {
        if (isEdit) {
            if (selectedMember && String(selectedMember.id) === String(id)) {
                dispatch(fetchCity(selectedMember.country_id)).then(() => {
                    dispatch(fetchOffice(selectedMember.city_id)).then(() => {
                        reset({
                            ...selectedMember,
                            permissions: selectedMember.permissions || [],
                        });
                        setIsShowLoader(false);
                    });
                });
            }
        } else {
            reset(initialForm);
        }
    }, [isEdit, id, selectedMember, dispatch]);

    useEffect(() => {
        let role = roles?.find(opt => opt.label === type);
        if (role) {
            setValue('role_id', role?.value, { shouldValidate: true });
            dispatch(fetchRolePermission(role?.value));
        }
    }, [roles, dispatch]);

    const idProof = watch("id_proof");
    const profileImage = watch('profile_image');

    const onSubmit = async (data) => {
        try {
            const result = await dispatch(addMember(data)).unwrap();
            if (result.status) {
                Toaster.success(result.message || t('toaster.userAddedSuccess'));
                dispatch(resetStatus());
                reset();
                navigate(`/dashboard/members`);
            } else {
                Toaster.error(result.message || t('toaster.somethingWentWrong'));
                dispatch(resetStatus());
                if (result?.data) {
                    setError(result?.data?.field, {
                        type: "manual",
                        message: result?.data?.message,
                    });
                }
            }
        } catch (err) {
            Toaster.error(err?.message || t('toaster.failedToAddUser'));
            dispatch(resetStatus());
        }
    };

    const removeImage = (field) => {
        setValue(field, null, { shouldValidate: false });
        if (isEdit) {
            isImageRemoved[field] = true;
        }
    }

    const handlePermissionChange = (permission_name, is_check) => {
        if (is_check === "no") {
            let permissions = watch("permissions") || [];
            if (permissions.includes(permission_name)) {
                permissions = permissions.filter((p) => p !== permission_name);
            } else {
                permissions = [...permissions, permission_name];
            }
            setValue("permissions", permissions);
        }
    };

    const handleCheckAllPermission = (e, perms) => {
        let currentPermissions = watch("permissions") || [];
        if (e.target.checked) {
            const newPermissions = perms
                .filter((perm) => perm.is_check === "no" && !currentPermissions.includes(perm.name))
                .map((perm) => perm.name);

            setValue("permissions", [...currentPermissions, ...newPermissions]);
        } else {
            const remainingPermissions = currentPermissions.filter(
                (permName) => !perms.some((perm) => perm.name === permName && perm.is_check === "no")
            );
            setValue("permissions", remainingPermissions);
        }
    };

    const isAllPermissionChecked = (permissions, watchedPermissions = []) => {
        return permissions.every(
            (perm) =>
                perm.is_check === "yes" || watchedPermissions.includes(perm.name)
        );
    };

    return (
        <div className="mt-5 mb-8 flex flex-col gap-8 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {(loading || isShowLoader) && <ShowLoader />}
             <ScrollToTop />
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-2">
                <div className="sticky top-0 z-30 mt-1 mb-6 p-5 bg-blue-gray-f5">
                    <div className="flex flex-col md:flex-row items-center justify-between gap-4 md:gap-2">
                        <div className="flex items-center gap-2 cursor-pointer">
                            <Typography variant="h5" color="blue-gray" className="font-extrabold text-gray-800">
                                {isView ? t('viewMemberTitle') : isEdit ? t('editMemberTitle') : t('addMemberTitle')}
                            </Typography>
                        </div>
                        {!isView && <div className="flex items-center gap-2">
                            <Button
                                type="button"
                                variant="outlined"
                                onClick={() => navigate(`/dashboard/members`)}
                                className="rounded-lg"
                            >
                                {ct('cancel')}
                            </Button>
                            <Button
                                type="submit"
                                disabled={loading}
                                className="flex items-center gap-2 border-gray-300 capitalize font-bold bg-black text-white">
                                {loading ? ct('saving') : isEdit ? ct('update') : ct('save')}
                            </Button>
                        </div>}
                    </div>
                    <div className="flex items-center gap-1">
                        <Link to={`/dashboard/members`} className="flex items-center text-blue-600 hover:text-blue-800">
                            <ArrowLeftIcon className="h-4 w-4 mr-1" />
                            {ct('back')}
                        </Link>
                    </div>
                </div>
                <fieldset disabled={isView}>
                    <div className="mt-1 mb-6 border border-blue-gray-100 rounded-lg p-5 bg-white">
                        <div className="mb-5 pb-2 border-b border-blue-gray-200">
                            <Typography variant="h4" color="blue-gray">
                                {t('basicUserDetails')}
                            </Typography>
                        </div>
                        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                            <div className="flex flex-col gap-1">
                                <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
                                    {t('fullNameLabel')} <span className="text-red-500">*</span>
                                </Typography>
                                <Input
                                    type="text"
                                    size="lg"
                                    placeholder={t('fullNamePlaceholder')}
                                    className=" !border-t-blue-gray-200 focus:!border-t-gray-900"
                                    labelProps={{
                                        className: "before:content-none after:content-none",
                                    }}
                                    {...register("first_name")}
                                    error={Boolean(errors.first_name)}
                                />
                                {errors.first_name && (
                                    <p className="text-red-500 text-sm">{errors.first_name?.message}</p>
                                )}
                            </div>
                            <div className="flex flex-col gap-1">
                                <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
                                    {t('emailAddressLabel')} <span className="text-red-500">*</span>
                                </Typography>
                                <Input
                                    type="email"
                                    size="lg"
                                    placeholder={t('emailAddressPlaceholder')}
                                    className=" !border-t-blue-gray-200 focus:!border-t-gray-900"
                                    labelProps={{
                                        className: "before:content-none after:content-none",
                                    }}
                                    {...register("email")}
                                    error={Boolean(errors.email)}
                                />
                                {errors.email && (
                                    <p className="text-red-500 text-sm">{errors.email?.message}</p>
                                )}
                            </div>
                            <div className="xl:col-span-1 flex flex-col gap-1">
                                <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
                                    {t('phoneNumberLabel')} <span className="text-red-500">*</span>{t('withWhatsApp')}
                                </Typography>
                                <div className="grid grid-cols-1 md:grid-cols-6 gap-0">
                                    <div className="md:col-span-3">
                                        <Select
                                            type="text"
                                            size="lg"
                                            className="!border-t-blue-gray-200 focus:!border-t-gray-900"
                                            labelProps={{
                                                className: "before:content-none after:content-none",
                                            }}
                                            placeholder={t('selectPlaceholder')}
                                            onChange={(code) => {
                                                setValue('country_code', code?.value, { shouldValidate: true });
                                            }}
                                            value={mobile_country_code?.find(opt => opt.value === watch('country_code')) || null}
                                            options={mobile_country_code}
                                            error={Boolean(errors.country_code)}
                                            isDisabled={isView}
                                            isClearable
                                            styles={{
                                                control: (provided, state) => ({
                                                    ...provided,
                                                    minHeight: '44px',
                                                    height: '44px',
                                                }),
                                            }}
                                            components={{
                                                Option: CountryOption,
                                                SingleValue: CountrySingleValue
                                            }}
                                        />
                                    </div>
                                    <div className="md:col-span-3">
                                        <Input
                                            type="number"
                                            size="lg"
                                            min='0'
                                            placeholder={t('phoneNumberPlaceholder')}
                                            className=" !border-t-blue-gray-200 focus:!border-t-gray-900"
                                            labelProps={{
                                                className: "before:content-none after:content-none",
                                            }}
                                            {...register("mobile_number")}
                                            error={Boolean(errors.mobile_number)}
                                        />
                                    </div>
                                </div>
                                {(errors.mobile_number || errors.country_code) && (
                                    <p className="text-red-500 text-sm">{errors.mobile_number?.message ? errors.mobile_number?.message : errors.country_code.message}</p>
                                )}
                            </div>
                            <div className="xl:col-span-1 flex flex-col gap-1">
                                <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
                                    {t('alternateNumberLabel')}
                                </Typography>
                                <div className="grid grid-cols-1 md:grid-cols-6 gap-0">
                                    <div className="md:col-span-3">
                                        <Select
                                            type="text"
                                            placeholder={t('selectPlaceholder')}
                                            onChange={(code) => {
                                                setValue('alternate_country_code', code?.value, { shouldValidate: true });
                                            }}
                                            value={mobile_country_code?.find(opt => opt.value === watch('alternate_country_code')) || null}
                                            options={mobile_country_code}
                                            error={Boolean(errors.alternate_country_code)}
                                            isDisabled={isView}
                                            isClearable
                                            styles={{
                                                control: (provided, state) => ({
                                                    ...provided,
                                                    minHeight: '44px',
                                                    height: '44px',
                                                }),
                                            }}
                                            components={{
                                                Option: CountryOption,
                                                SingleValue: CountrySingleValue
                                            }}
                                        />
                                    </div>
                                    <div className="md:col-span-3">
                                        <Input
                                            type="number"
                                            size="lg"
                                            min='0'
                                            placeholder={t('alternateNumberPlaceholder')}
                                            className=" !border-t-blue-gray-200 focus:!border-t-gray-900"
                                            labelProps={{
                                                className: "before:content-none after:content-none",
                                            }}
                                            {...register("alternate_number")}
                                            error={Boolean(errors.alternate_number)}
                                            onChange={(e) => {
                                                let value = e.target.value;
                                                if (!value || value.trim() === "") {
                                                    setValue("alternate_number", '', { shouldValidate: true });
                                                    setValue("alternate_country_code", null, { shouldValidate: true });
                                                }
                                            }}
                                        />
                                    </div>
                                </div>
                                {(errors.alternate_number || errors.alternate_country_code) && (
                                    <p className="text-red-500 text-sm">{errors.alternate_number?.message ? errors.alternate_number?.message : errors.alternate_country_code.message}</p>
                                )}
                            </div>
                            <div className="flex flex-col gap-1">
                                <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
                                    {t('profilePhotoLabel')} <span className="text-red-500">*</span>
                                </Typography>
                                <div className="flex items-center gap-2">
                                    {!profileImage ?
                                        <div className="w-16 h-16 flex items-center justify-center rounded-full border-2 border-dashed border-gray-300 ml-3 mr-5">
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                className="w-6 h-6 text-gray-400"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                                stroke="currentColor"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={1.5}
                                                    d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zM4 20c0-2.67 5.33-4 8-4s8 1.33 8 4"
                                                />
                                            </svg>
                                        </div>

                                        : <div className="relative w-16 h-16 ml-3 mr-5">
                                            <FilePreview
                                                file={profileImage}
                                                onRemove={() => removeImage('profile_image')}
                                                isView={isView}
                                                containerClass="relative w-16 h-16 rounded-full border-2 border-gray-300 overflow-hidden flex items-center justify-center bg-gray-50"
                                            />
                                        </div>
                                    }
                                    <input
                                        id="profilePhoto"
                                        type="file"
                                        accept="image/*"
                                        multiple
                                        className="hidden"
                                        onChange={(e) => {
                                            const file = e.target.files[0] || null;
                                            setValue('profile_image', file, { shouldValidate: true });
                                        }}
                                    /><p className="text-xs text-gray-400 mt-1">{t('fileUploadHint')}</p>
                                    <label
                                        htmlFor="profilePhoto"
                                        className="h-11 w-48 cursor-pointer px-4 py-2 bg-green-100 text-green-800 border border-green-300 rounded-md hover:bg-green-200 text-sm font-medium flex items-center justify-center gap-2"
                                    >
                                        <DocumentPlusIcon className="h-4 w-4 cursor-pointer text-green-800"></DocumentPlusIcon> {ct('chooseFile')}
                                    </label>
                                </div>
                                {errors.profile_image && (
                                    <p className="text-red-500 text-sm mt-2">{errors.profile_image?.message}</p>
                                )}
                            </div>
                            <div className="flex flex-col gap-1">
                                <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
                                    {t('residenceIdLabel')} <span className="text-red-500">*</span>
                                </Typography>
                                <div className="flex items-center gap-2">
                                    <Input
                                        type="number"
                                        size="lg"
                                        placeholder={t('residenceIdPlaceholder')}
                                        className=" !border-t-blue-gray-200 focus:!border-t-gray-900"
                                        labelProps={{
                                            className: "before:content-none after:content-none",
                                        }}
                                        {...register("residence_id")}
                                        error={Boolean(errors.residence_id)}
                                    />

                                    <input
                                        id="residenceNumberFile"
                                        type="file"
                                        accept="image/*"
                                        multiple
                                        className="hidden"
                                        onChange={(e) => {
                                            const file = e.target.files[0] || null;
                                            setValue('id_proof', file, { shouldValidate: true });
                                        }}
                                    />
                                    <label
                                        htmlFor="residenceNumberFile"
                                        className="h-11 w-48 cursor-pointer px-4 py-2 bg-green-100 text-green-800 border border-green-300 rounded-md hover:bg-green-200 text-sm font-medium flex items-center justify-center gap-2"
                                    >
                                        <DocumentPlusIcon className="h-4 w-4 cursor-pointer text-green-800"></DocumentPlusIcon> {ct('chooseFile')}
                                    </label>
                                </div>
                                <p className="text-xs text-gray-400">{t('fileUploadHint')} </p>
                                {(errors.residence_id?.message || errors.id_proof?.message) && (
                                    <p className="text-red-500 text-sm mt-2">
                                        {errors.residence_id?.message || errors.id_proof?.message}
                                    </p>
                                )}
                            </div>
                            {idProof && <>
                                <div className="flex flex-col gap-1"></div>
                                <div className="flex flex-col gap-1">
                                    <FilePreview
                                        file={idProof}
                                        onRemove={() => removeImage('id_proof')}
                                        isView={isView}
                                        containerClass="relative w-24 h-24 rounded-md overflow-hidden border border-gray-300"
                                    />
                                </div>
                            </>}
                        </div>
                    </div>
                    <div className="mt-1 mb-6 border border-blue-gray-100 rounded-lg p-5 bg-white">
                        <div className="mb-5 pb-2 border-b border-blue-gray-200">
                            <Typography variant="h4" color="blue-gray">
                                {t('assignOfficeTitle')}
                            </Typography>
                        </div>
                        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                            <div className="md:col-span-2 w-full flex items-center gap-3">
                                <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
                                    {t('isOfficeIncharge')}
                                </Typography>

                                <label className="flex items-center gap-1 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        value="office"
                                        {...register("sales_work_type")}
                                        checked={watch('sales_work_type') == 'office'}
                                        className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                    />
                                    <span className="text-gray-700 text-sm">{ct('yes')}</span>
                                </label>
                            </div>
                            <div className="flex flex-col gap-1">
                                <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
                                    {t('countryLabel')} <span className="text-red-500">*</span>
                                </Typography>
                                <Select
                                    placeholder={t('selectCountryPlaceholder')}
                                    onChange={(country) => {
                                        setValue('country_id', country?.value, { shouldValidate: true });
                                        dispatch(fetchCity(country?.value));
                                    }}
                                    error={Boolean(errors.country_id)}
                                    options={countries}
                                    value={countries?.find(opt => opt.value === watch('country_id')) || null}
                                    isClearable
                                    isDisabled={isView}
                                />
                                {errors.country_id && (
                                    <p className="text-red-500 text-sm">{errors.country_id?.message}</p>
                                )}
                            </div>

                            <div className="flex flex-col gap-1">
                                <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
                                    {t('cityTerritoryLabel')} <span className="text-red-500">*</span>
                                </Typography>
                                <Select
                                    placeholder={t('selectCityPlaceholder')}
                                    onChange={(city) => {
                                        setValue('city_id', city?.value, { shouldValidate: true });
                                        dispatch(fetchOffice(city?.value))
                                    }}
                                    error={Boolean(errors.city_id)}
                                    options={cities}
                                    value={cities?.find(opt => opt.value === watch('city_id')) || null}
                                    isClearable
                                    isDisabled={isView}
                                />
                                {errors.city_id && (
                                    <p className="text-red-500 text-sm">{errors.city_id?.message}</p>
                                )}
                            </div>

                            <div className="flex flex-col gap-1">
                                <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
                                    {t('officeLabel')} <span className="text-red-500">*</span>
                                </Typography>
                                <Select
                                    placeholder={t('selectOfficePlaceholder')}
                                    onChange={(office) => {
                                        setValue('office_id', office?.value, { shouldValidate: true });
                                    }}
                                    error={Boolean(errors.office_id)}
                                    options={offices}
                                    value={offices?.find(opt => opt.value === watch('office_id')) || null}
                                    isClearable
                                    isDisabled={isView}
                                />
                                {errors.office_id && (
                                    <p className="text-red-500 text-sm">{errors.office_id?.message}</p>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="mt-1 mb-6 border border-blue-gray-100 rounded-lg p-5 bg-white">
                        <div className="mb-5 pb-2 border-b border-blue-gray-200">
                            <Typography variant="h4" color="blue-gray">
                                {t('assignRoleTitle')}
                            </Typography>
                        </div>
                        <div className="px-0 pt-0 pb-2 p-6">
                            <div className="mb-4 w-full flex items-center gap-3">
                                <Typography variant="small" color="blue-gray" className="font-medium">
                                    {t('isMemberRole')}
                                </Typography>

                                <label className="flex items-center gap-1 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        value="member"
                                        {...register("designation")}
                                        className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        disabled
                                    />
                                    <span className="text-gray-700 text-sm">{ct('yes')}</span>
                                </label>
                            </div>
                            {/* <div className="mb-4 w-full grid grid-cols-1 md:grid-cols-2 gap-6"> */}
                            <div className="flex flex-col gap-2">
                                <Typography variant="small" color="blue-gray" className="font-medium">
                                    {t('selectRoleLabel')} <span className="text-red-500">*</span>
                                </Typography>
                                <Select
                                    placeholder={t('selectRolePlaceholder')}
                                    error={Boolean(errors.role_id)}
                                    options={roles}
                                    value={roles?.find(opt => opt.label === type)}
                                    isDisabled={true}
                                />
                                {errors.role_id && (
                                    <p className="text-red-500 text-sm">{errors.role_id?.message}</p>
                                )}
                            </div>
                            {/* </div> */}
                        </div>
                    </div>
                    <div className="mt-1 mb-6 border border-blue-gray-100 rounded-lg p-5 bg-white">
                        <div className="mb-5 pb-2 border-b border-blue-gray-200">
                            <Typography variant="h4" color="blue-gray">
                                {t('assignPermissionsTitle')}
                            </Typography>
                            <Typography variant="small" color="gray" className="-mb-3 font-medium">
                                {t('preconfiguredHint')}
                            </Typography>
                        </div>
                        <div className="px-0 pt-0 pb-2 p-6">
                            <table className="w-full min-w-max table-auto text-left border border-gray-200">
                                <thead>
                                    <tr>
                                        <th className="p-4 border-b border-gray-200">{t('module')}</th>
                                        <th className="p-4 border-b border-gray-200 text-center">{t('all')}</th>
                                        <th className="p-4 border-b border-gray-200 text-center">{t('view')}</th>
                                        <th className="p-4 border-b border-gray-200 text-center">{t('add')}</th>
                                        <th className="p-4 border-b border-gray-200 text-center">{t('update')}</th>
                                        <th className="p-4 border-b border-gray-200 text-center">{t('delete')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {role_permissions?.permissions && Object.entries(role_permissions?.permissions).map(([module, perms]) => {
                                        return (
                                            <tr key={module} className="hover:bg-gray-50">
                                                <td className="p-4 font-medium">{t(module)}</td>
                                                <td className="p-4 text-center">
                                                    <input
                                                        type="checkbox"
                                                        value="yes"
                                                        onChange={(e) => {
                                                            handleCheckAllPermission(e, perms);
                                                        }}
                                                        checked={isAllPermissionChecked(perms, watch("permissions"))}
                                                        disabled={isAllPermissionChecked(perms, register.permissions)}
                                                        className="w-4 h-4 cursor-pointer accent-green"
                                                    />
                                                    <p className="capitalize text-gray-500 text-sm">
                                                        {t('all')}
                                                    </p>
                                                </td>
                                                {perms.map((perm) => (
                                                    <td key={perm.name} className="p-4 text-center">
                                                        <input
                                                            type="checkbox"
                                                            value="yes"
                                                            checked={perm.is_check === "yes" || watch('permissions').includes(perm.name)}
                                                            disabled={perm.is_check === "yes"}
                                                            className={`w-4 h-4 cursor-pointer ${perm.is_check === "yes" ? "accent-gray-400 cursor-not-allowed" : "accent-green"
                                                                }`}
                                                            onChange={() => {
                                                                if (perm.is_check === "no") {
                                                                    handlePermissionChange(perm.name, perm.is_check);
                                                                }
                                                            }}
                                                        />
                                                        <p className="capitalize text-gray-500 text-sm">
                                                            {t(perm.name.split('.').pop() !== 'update' ? perm.name.split('.').pop() : 'edit')}
                                                        </p>
                                                    </td>
                                                ))}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </fieldset>
            </form>
        </div>
    );
}

export default AddMember;